import { AttackPattern, PatternState } from '../AttackPattern.js';

/**
 * Petrifying Gaze (The Unblinking) — inverts the genre's core verb: while
 * the eye is open, MOVING is what hurts you. Any tick the player is
 * displacing (beyond a small jitter deadzone) eats a rate-limited damage
 * tick; standing still is perfectly safe. Dash i-frames cheat the gaze
 * (the sanctioned way to reposition mid-stare), and slow homing "tears"
 * drip toward a stationary player so holding still is a real choice with
 * a clock on it, not a free win. The dilemma sharpens once the boss's
 * layering phase arrives and a second pattern demands movement at the
 * same time.
 */
export class PetrifyingGaze extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Petrifying Gaze', telegraphDuration: 1.5, activeDuration: 2.4, minPhase, arenaControl: true });
    this.config = config;
  }

  onBegin(boss) {
    boss.locked = true; // the eye holds perfectly still to stare
    this._punishTimer = 0;
    this._tearTimer = 0.7;
  }

  onActiveStart(_boss, player) {
    this._prevX = player.x;
    this._prevY = player.y;
  }

  onActiveTick(dt, boss, player, pool) {
    const moved = Math.hypot(player.x - this._prevX, player.y - this._prevY);
    this._prevX = player.x;
    this._prevY = player.y;
    this._punishTimer -= dt;

    // Deadzone of 18 px/s — well under real movement (72 px/s) but above
    // any knockback/current jitter, so only deliberate motion is punished.
    if (moved > 18 * dt && !player.isInvincible && this._punishTimer <= 0) {
      player.takeDamage(this.config.gazeDamage);
      this._punishTimer = 0.55;
    }

    this._tearTimer -= dt;
    if (this._tearTimer <= 0) {
      this._tearTimer = this.config.tearInterval;
      const angle = Math.atan2(player.y - boss.y, player.x - boss.x);
      pool.fire({
        x: boss.x,
        y: boss.y,
        vx: Math.cos(angle) * this.config.tearSpeed,
        vy: Math.sin(angle) * this.config.tearSpeed,
        radius: 3,
        damage: this.config.tearDamage,
        color: '#8ae06e',
        lifetime: 4,
        homing: true,
        turnRate: 1.1,
        target: player,
      });
    }
  }

  onEnd(boss) {
    boss.locked = false;
    // Surviving the stare earns a clean punish window — the eye blinks.
    boss.openVulnerability(0.7, 1.5);
  }

  getVisual(boss) {
    if (this.state === PatternState.TELEGRAPH) {
      return { kind: 'gaze', x: boss.x, y: boss.y, t: 1 - this._timer / this.telegraphDuration, active: false };
    }
    if (this.state === PatternState.ACTIVE) {
      return { kind: 'gaze', x: boss.x, y: boss.y, t: 1, active: true };
    }
    return null;
  }
}
